import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { studentTermReportActions as slice } from "../slices/studentTermReport";
import { getErrorMessage } from "../../utils/errorUtils";
import { StudentTermReportResponseDto } from "../../shared/dtos/studentTermReport/StudentTermReportResponseDto";
import { StudentTermReportUpdateRequestDto } from "../../shared/dtos/studentTermReport/StudentTermReportUpdateRequestDto";
import { StudentTermReportGenerationRequestDto } from "../../shared/dtos/studentTermReport/StudentTermReportGenerationRequestDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";

export interface StudentTermReportQueryParams extends PaginationQueryDto {
  branchId?: string;
  academicYearId?: string;
  termId?: string;
  classroomId?: string;
  studentId?: string;
}

export const generateBatchReports =
  (values: StudentTermReportGenerationRequestDto) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingGeneration());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        "/student-term-reports/generate",
        values,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(
          data.message || "Reports generated and ranked successfully!",
        );
        dispatch(slice.generateReportsSuccess());
        dispatch(
          slice.setServerResponseStatus({
            operation: "generate",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(slice.setErrorGenerating(message));
    }
  };

export const getStudentsTermReports =
  (params: StudentTermReportQueryParams = { page: 1, size: 15 }) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingReports());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<StudentTermReportResponseDto>>
      >("/student-term-reports", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(slice.setReports(data.data.content));
        dispatch(slice.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(slice.setErrorLoadingReports(getErrorMessage(error)));
    }
  };

export const getStudentTermReportById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingReport());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<StudentTermReportResponseDto>
      >(`/student-term-reports/${id}`, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(slice.setReport(data.data));
      }
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(slice.setErrorLoadingReport(message));
    }
  };

export const updateStudentTermReport =
  (payload: { id: string; values: StudentTermReportUpdateRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<StudentTermReportResponseDto>
      >(`/student-term-reports/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Report updated successfully!");
        dispatch(slice.updateReportSuccess(data.data));
        dispatch(
          slice.setServerResponseStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(slice.setErrorUpdating(message));
    }
  };

export const dispatchReportsToClassroom =
  (params: { classroomId: string; termId: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingDispatch());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        "/student-term-reports/dispatch/classroom",
        null,
        {
          headers: { requiresAuth: true },
          params,
        },
      );

      if (
        data.status === ApiResponseStatus.SUCCESS ||
        data.statusCode === 202
      ) {
        toast.info(
          data.message || "Report dispatch initiated in the background.",
        );
        dispatch(slice.dispatchReportsSuccess());
        dispatch(
          slice.setServerResponseStatus({
            operation: "dispatch",
            status: data.statusCode || 202,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorDispatching(message));
    }
  };

export const publishBatchReports =
  (params: { classroomId: string; termId: string; publish: boolean }) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingPublish());
    try {
      const { data } = await axiosInstance.patch<ApiResponseDto<void>>(
        "/student-term-reports/publish",
        null,
        {
          headers: { requiresAuth: true },
          params,
        },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(
          data.message ||
            `Reports ${params.publish ? "published" : "hidden"} successfully!`,
        );
        dispatch(slice.publishReportsSuccess({ publish: params.publish }));
        dispatch(
          slice.setServerResponseStatus({
            operation: "publish",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorPublish(message));
    }
  };

export const resetReportServerStatus = () => (dispatch: AppDispatch) =>
  dispatch(slice.setServerResponseStatus(null));
